import type { EventRow } from '@/types/database';

/** Build a minimal RFC 5545 calendar invite. */
export function buildIcs(event: EventRow, organizerName?: string | null): string {
  const dt = (iso: string) => {
    const d = new Date(iso);
    return (
      d.getUTCFullYear().toString() +
      pad(d.getUTCMonth() + 1) +
      pad(d.getUTCDate()) +
      'T' +
      pad(d.getUTCHours()) +
      pad(d.getUTCMinutes()) +
      pad(d.getUTCSeconds()) +
      'Z'
    );
  };
  const endsAt = event.ends_at ?? new Date(new Date(event.starts_at).getTime() + 3600_000).toISOString();
  const uid = `${event.id}@haphe`;

  const rrule = rruleFor(event.recurrence);

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Haphe//gethaphe.com//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dt(new Date().toISOString())}`,
    `DTSTART:${dt(event.starts_at)}`,
    `DTEND:${dt(endsAt)}`,
    `SUMMARY:${esc(event.name)}`,
    event.description ? `DESCRIPTION:${esc(event.description)}` : null,
    event.location ? `LOCATION:${esc(event.location)}` : null,
    event.url ? `URL:${esc(event.url)}` : null,
    rrule ? `RRULE:${rrule}` : null,
    organizerName ? `ORGANIZER;CN="${esc(organizerName)}":MAILTO:noreply@haphe` : null,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);
  return lines.join('\r\n') + '\r\n';
}

export function downloadIcs(event: EventRow, organizerName?: string | null) {
  const content = buildIcs(event, organizerName);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slugForFilename(event.name)}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}
function rruleFor(recurrence: EventRow['recurrence']): string | null {
  switch (recurrence) {
    case 'weekly':
      return 'FREQ=WEEKLY';
    case 'monthly':
      return 'FREQ=MONTHLY';
    case 'yearly':
      return 'FREQ=YEARLY';
    default:
      return null;
  }
}
export function recurrenceLabel(
  recurrence: EventRow['recurrence'],
  startsAt: string,
): string | null {
  if (!recurrence || recurrence === 'none') return null;
  const d = new Date(startsAt);
  switch (recurrence) {
    case 'weekly':
      return `Every ${d.toLocaleDateString(undefined, { weekday: 'long' })}`;
    case 'monthly':
      return `Monthly · day ${d.getDate()}`;
    case 'yearly':
      return `Yearly · ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
    default:
      return null;
  }
}
function esc(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
}
function slugForFilename(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '') || 'event';
}
